import React, { useState } from 'react';
import { Home, Calendar, Users, Activity, CreditCard, HelpCircle } from 'lucide-react';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const navigationItems = [
  { id: 'dashboard', label: 'Dashboard', icon: Home },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'pulse', label: 'RollOps Pulse', icon: Activity },
  { id: 'billing', label: 'Billing & Subscriptions', icon: CreditCard },
];

// Shortened labels for the mobile bottom nav
const mobileLabels: Record<string, string> = {
  dashboard: 'Home',
  schedule: 'Schedule',
  students: 'Students',
  pulse: 'Pulse',
  billing: 'Billing',
};

export default function Sidebar({ activeSection, onSectionChange }: SidebarProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Sidebar is collapsed by default, expands on hover
  const isExpanded = isHovered;

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile */}
      <div 
        className={`hidden md:block fixed top-16 left-0 bottom-0 bg-[#1a1818] border-r border-white/10 transition-all duration-300 z-40 overflow-hidden ${
          isExpanded ? 'w-64' : 'w-20'
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Navigation Items */}
          <nav className="flex-1 pt-3 pb-6 px-3 space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => onSectionChange(item.id)}
                  className={`
                    w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all relative group overflow-hidden
                    ${isActive 
                      ? 'bg-rollops-primary/20 text-text-primary' 
                      : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                    }
                  `}
                  title={!isExpanded ? item.label : undefined}
                >
                  {/* Active Indicator Bar */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-rollops-primary rounded-r" />
                  )}
                  
                  {/* Icon */}
                  <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-rollops-primary' : ''}`} />
                  
                  {/* Label - fades in/out and clips with sidebar expansion */}
                  <span 
                    className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                      isExpanded ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Contact Support Button */}
          <div className="px-3 pb-6">
            <button
              onClick={() => window.open('mailto:support@rollops.pro', '_blank')}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all overflow-hidden text-text-secondary hover:bg-white/5 hover:text-text-primary"
              title={!isExpanded ? 'Contact Support' : undefined}
            >
              <HelpCircle className="w-5 h-5 shrink-0" />
              <span 
                className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                  isExpanded ? 'opacity-100' : 'opacity-0'
                }`}
              >
                Contact Support
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Visible only below md */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#1a1818] border-t border-white/10 z-40">
        <div className="flex items-center justify-around px-1 pt-2 pb-[env(safe-area-inset-bottom,8px)]">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => onSectionChange(item.id)}
                className={`
                  flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors relative min-w-0
                  ${isActive 
                    ? 'text-rollops-primary' 
                    : 'text-text-secondary active:text-text-primary'
                  }
                `}
              >
                {/* Active top indicator */}
                {isActive && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-rollops-primary rounded-b" />
                )}
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                  {mobileLabels[item.id] ?? item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
